/**
 * Base API client for the Feature Release Manager.
 *
 * All APIs return errors in this shape:
 * { error: { code, message, details? }, meta: { requestId, timestamp } }
 */
import { API_BASE_URL, HEADERS } from "../constants";

/**
 * Core fetch wrapper. Throws a descriptive Error on non-2xx responses.
 * @param {string} path - e.g. "/api/releases?limit=20"
 * @param {RequestInit} options - fetch options
 * @returns {Promise<any>} - parsed JSON body
 */
export async function apiFetch(path, options = {}) {
    const url = `${API_BASE_URL}${path}`;

    // Only include Content-Type when we're actually sending a body
    const baseHeaders = options.body
        ? HEADERS
        : { "x-api-key": HEADERS["x-api-key"] };

    const response = await fetch(url, {
        ...options,
        headers: {
            ...baseHeaders,
            ...(options.headers || {}),
        },
    });

    let body;
    try {
        body = await response.json();
    } catch {
        const err = new Error(`HTTP ${response.status}: Could not parse response.`);
        err.status = response.status;
        err.details = null;
        throw err;
    }

    if (!response.ok) {
        // API returns { error: { code, message } }
        const errCode = body?.error?.code || response.status;
        const errMsg = body?.error?.message || `Request failed (${response.status})`;
        const err = new Error(`${errCode}: ${errMsg}`);
        err.status = response.status;
        err.details = body?.error?.details || null;
        throw err;
    }

    return body;
}

/**
 * Build a URL path with optional query parameters.
 * @param {string} base
 * @param {Record<string,string|number|undefined>} params
 */
export function buildUrl(base, params = {}) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== "") {
            search.set(key, String(value));
        }
    }
    const qs = search.toString();
    return qs ? `${base}?${qs}` : base;
}
